import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  acceptTitleSuggestion, dismissTitleSuggestion, downloadMaterial, fetchMaterials, publishMaterial,
  publishSharedDeck, reviewMaterial, unpublishMaterial, unpublishSharedDeck,
} from './ProgramAdminApi';
import ConfirmDialog from './ConfirmDialog';
import { formatDateTime } from './programAdminModel';
import { ConsentTag } from './ui';
import {
  HistoryWhen, MiniButton, PanelHint, PanelLabel, PanelRow, PanelSection, PanelSectionTitle, PersonMeta,
  PersonName, SpotActions,
} from './programAdminStyles';

// The talk materials of one program entry (design §4.7, plan §7.7): the deck versions an admin may
// look at and publish, the speakers' consent answers and the title a person suggested on their own
// talk. It owns its own reads and writes through ./ProgramAdminApi (the ImportsArea pattern) and sits
// in the entry panel next to the entry's time, room and spots; it renders for a removed entry too,
// because what was uploaded and published stays worth reading.
//
// The admin API answers ids, never names (`speakers[].personId`, `titleSuggestedByPersonId`), and this
// panel prints them as they come.
//
// Every call follows the same shape: a 4xx/5xx body is the plain-text message the admin should read,
// a 401 sends them to the login (as on the other admin screens), and any write reloads the talk and
// says what happened.

// A file size a human reads at a glance: bytes under 1 KB, then KB, then MB.
const formatSize = (bytes) => {
  if (typeof bytes !== 'number' || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// The published/consent tag tones (ConsentTag in ./ui).
const consentTone = (consent) => {
  if (consent === 'GRANTED') return 'granted';
  if (consent === 'DENIED') return 'denied';
  return 'unset';
};

// Deployments come newest last from the backend; the admin reads a version history from the first
// upload down, so the oldest deck is printed first.
const oldestFirst = (decks) => [...(decks || [])]
  .sort((a, b) => (a.uploadedAt || '').localeCompare(b.uploadedAt || ''));

const nonEmpty = (list) => (list || []).filter((value) => !!value);

// `onChanged` is how the panel tells its owner (the program screen) that the program itself moved:
// taking a suggested title overwrites the entry's title, which the Edit dialog and the program list
// show, so without it they would keep printing the old one until the next manual reload.
const SlidesPanel = ({ eventId, entryId, onChanged }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [talk, setTalk] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // The action waiting for a confirmation (plan §7.7 item 7: the one dialog for actions with
  // consequences). `null` when nothing is asked; a spec is {titleId, titleDef, introId, introDef,
  // points, key, task, result}.
  const [confirming, setConfirming] = useState(null);

  // The materials of the event, of which this panel keeps the row of its own entry. Dependencies:
  // `eventId`/`entryId` (which talk this is - given as strings from the route, so the row is matched
  // by number), `reloadKey` (bumped after every write), `intl` (the error texts) and `navigate` (the
  // 401 redirect).
  useEffect(() => {
    if (eventId == null || entryId == null) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    fetchMaterials(eventId)
      .then((rows) => {
        if (!live) return;
        const wanted = Number(entryId);
        setTalk((rows || []).find((row) => row.entryId === wanted) || null);
      })
      .catch((err) => {
        if (!live) return;
        if (err.response?.status === 401) { navigate('/admin/login'); return; }
        setError(typeof err.response?.data === 'string' && err.response.data
          ? err.response.data
          : intl.formatMessage({ id: 'programAdmin.slides.loadFailed', defaultMessage: 'Could not load the talk materials.' }));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [eventId, entryId, reloadKey, intl, navigate]);

  // One action at a time: `busy` names the running one, a write bumps `reloadKey` and shows its
  // success line, and every failure lands in the same place (nothing is changed on screen).
  const run = async (key, task, { successId, successDef, failedId, failedDef, reload, changed }) => {
    setBusy(key);
    setActionError(null);
    setSuccess(null);
    try {
      await task();
      if (successId) setSuccess(intl.formatMessage({ id: successId, defaultMessage: successDef }));
      if (reload) setReloadKey((k) => k + 1);
      // A write that moved the program itself (a title taken or dismissed) tells the owner, which
      // re-reads it; a refusal must not, so this stays inside the try.
      if (changed) onChanged?.();
      return true;
    } catch (err) {
      if (err.response?.status === 401) { navigate('/admin/login'); return false; }
      setActionError(typeof err.response?.data === 'string' && err.response.data
        ? err.response.data
        : intl.formatMessage({ id: failedId, defaultMessage: failedDef }));
      return false;
    } finally {
      setBusy(null);
    }
  };

  // Confirmed actions: the dialog stays open while the call runs (a refusal keeps it open with the
  // message) and closes once the write went through.
  const confirmed = async () => {
    const spec = confirming;
    if (!spec) return;
    if (await run(spec.key, spec.task, spec.result)) setConfirming(null);
  };

  const decks = talk ? oldestFirst(talk.decks) : [];
  const currentShared = talk && talk.mode === 'SHARED' ? decks.find((deck) => deck.current) : null;
  const sharedBlockers = nonEmpty(currentShared?.blockers);
  const disabled = busy !== null;

  return (
    <PanelSection>
      <PanelSectionTitle>
        <FormattedMessage id="programAdmin.slides.title" defaultMessage="Talk materials" />
      </PanelSectionTitle>

      {loading && (
        <PersonMeta><FormattedMessage id="programAdmin.slides.loading" defaultMessage="Loading the talk materials…" /></PersonMeta>
      )}

      {!loading && error && (
        <>
          <PanelHint style={{ color: '#991b1b' }}>{error}</PanelHint>
          <MiniButton type="button" onClick={() => setReloadKey((k) => k + 1)}>
            <FormattedMessage id="programAdmin.slides.retry" defaultMessage="Retry" />
          </MiniButton>
        </>
      )}

      {!loading && !error && !talk && (
        <PanelHint>
          <FormattedMessage id="programAdmin.slides.none" defaultMessage="No talk materials on this entry." />
        </PanelHint>
      )}

      {!loading && !error && talk && (
        <>
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.slides.mode" defaultMessage="Mode" /></PanelLabel>
            <span>
              {talk.mode === 'NONE'
                ? <FormattedMessage id="programAdmin.material.NONE" defaultMessage="No slides asked for" />
                : talk.mode === 'SHARED'
                  ? <FormattedMessage id="programAdmin.material.SHARED" defaultMessage="One shared deck" />
                  : <FormattedMessage id="programAdmin.material.PER_SPEAKER" defaultMessage="One deck per speaker" />}
            </span>
          </PanelRow>

          {/* An entry without a material mode has no deck, no consent and no speakers to consent: the
              one thing left to decide is the title a speaker suggested on it. */}
          {talk.mode === 'NONE' && (
            <PanelHint>
              <FormattedMessage id="programAdmin.slides.noSlides"
                defaultMessage="This entry asks for no slides, so there is nothing to upload or publish here. A title a speaker suggested on it is decided below." />
            </PanelHint>
          )}

          {talk.mode !== 'NONE' && (
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.slides.consent" defaultMessage="Speakers’ consent" /></PanelLabel>
            <div>
              {nonEmpty(talk.speakers).length === 0 && (
                <PersonMeta><FormattedMessage id="programAdmin.slides.consentNone" defaultMessage="No confirmed speaker yet." /></PersonMeta>
              )}
              {nonEmpty(talk.speakers).map((speaker) => (
                <div key={speaker.assignmentId} style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                  <span>#{speaker.personId}</span>
                  <ConsentTag $tone={consentTone(speaker.consent)}>{speaker.consent}</ConsentTag>
                  {speaker.consentAt && <PersonMeta as="span">{formatDateTime(intl, speaker.consentAt)}</PersonMeta>}
                </div>
              ))}
            </div>
          </PanelRow>
          )}

          {talk.mode !== 'NONE' && decks.length === 0 && (
            <PanelHint><FormattedMessage id="programAdmin.slides.noDecks" defaultMessage="No deck uploaded yet." /></PanelHint>
          )}

          {decks.map((deck, index) => {
            const blockers = nonEmpty(deck.blockers);
            const meta = [
              formatSize(deck.sizeBytes),
              deck.uploadedAt && intl.formatMessage({ id: 'programAdmin.slides.uploaded', defaultMessage: 'uploaded {when}' },
                { when: formatDateTime(intl, deck.uploadedAt) }),
              deck.current
                ? intl.formatMessage({ id: 'programAdmin.slides.current', defaultMessage: 'current' })
                : intl.formatMessage({ id: 'programAdmin.slides.superseded', defaultMessage: 'superseded' }),
              deck.published && (deck.publishedAt
                ? intl.formatMessage({ id: 'programAdmin.slides.publishedAt', defaultMessage: 'published {when}' },
                  { when: formatDateTime(intl, deck.publishedAt) })
                : intl.formatMessage({ id: 'programAdmin.slides.published', defaultMessage: 'published' })),
              // When this version was looked at - the "Review" button's own outcome, otherwise the
              // admin has no sign it did anything.
              deck.reviewedAt && intl.formatMessage({ id: 'programAdmin.slides.reviewedAt', defaultMessage: 'reviewed {when}' },
                { when: formatDateTime(intl, deck.reviewedAt) }),
            ].filter(Boolean).join(' · ');

            return (
              <PanelRow key={deck.id}>
                <PanelLabel>
                  <FormattedMessage id="programAdmin.slides.deck" defaultMessage="Deck {n}" values={{ n: index + 1 }} />
                </PanelLabel>
                <div>
                  <PersonName>{deck.fileName}</PersonName>
                  <PersonMeta>{meta}</PersonMeta>
                  {deck.ownerPersonId != null && (
                    <PersonMeta>
                      <FormattedMessage id="programAdmin.slides.owner" defaultMessage="Uploaded for speaker #{personId}" values={{ personId: deck.ownerPersonId }} />
                    </PersonMeta>
                  )}
                  {blockers.map((blocker) => (
                    <PersonMeta key={blocker} style={{ color: '#991b1b' }}>{blocker}</PersonMeta>
                  ))}
                  <SpotActions style={{ marginTop: '.35rem' }}>
                    <MiniButton type="button" disabled={disabled}
                      onClick={() => run(`download:${deck.id}`,
                        () => downloadMaterial(eventId, deck.id, deck.fileName),
                        { failedId: 'programAdmin.slides.downloadFailed', failedDef: 'Could not download the file.' })}>
                      <FormattedMessage id="programAdmin.slides.download" defaultMessage="Download" />
                    </MiniButton>
                    {deck.current && (
                      <MiniButton type="button" disabled={disabled}
                        onClick={() => run(`review:${deck.id}`,
                          () => reviewMaterial(eventId, deck.id),
                          {
                            successId: 'programAdmin.slides.reviewed', successDef: 'The upload was marked as reviewed.',
                            failedId: 'programAdmin.slides.reviewFailed', failedDef: 'Could not mark the upload as reviewed.',
                            reload: true,
                          })}>
                        <FormattedMessage id="programAdmin.slides.review" defaultMessage="Review" />
                      </MiniButton>
                    )}
                    {deck.current && (deck.published ? (
                      <MiniButton type="button" disabled={disabled}
                        onClick={() => setConfirming({
                          titleId: 'programAdmin.slides.unpublishConfirmTitle', titleDef: 'Unpublish this deck?',
                          introId: 'programAdmin.slides.unpublishConfirmIntro',
                          introDef: 'The deck stops being downloadable from the public program. The file and its history stay.',
                          key: `unpublish:${deck.id}`,
                          task: () => unpublishMaterial(eventId, deck.id),
                          result: {
                            successId: 'programAdmin.slides.unpublished', successDef: 'The deck was unpublished.',
                            failedId: 'programAdmin.slides.unpublishFailed', failedDef: 'Could not unpublish the deck.',
                            reload: true,
                          },
                        })}>
                        <FormattedMessage id="programAdmin.slides.unpublish" defaultMessage="Unpublish" />
                      </MiniButton>
                    ) : (
                      <MiniButton type="button" disabled={disabled || blockers.length > 0}
                        title={blockers.length > 0 ? blockers.join('\n') : undefined}
                        onClick={() => run(`publish:${deck.id}`,
                          () => publishMaterial(eventId, deck.id),
                          {
                            successId: 'programAdmin.slides.published', successDef: 'The deck was published.',
                            failedId: 'programAdmin.slides.publishFailed', failedDef: 'Could not publish the deck.',
                            reload: true,
                          })}>
                        <FormattedMessage id="programAdmin.slides.publish" defaultMessage="Publish" />
                      </MiniButton>
                    ))}
                  </SpotActions>
                </div>
              </PanelRow>
            );
          })}

          {talk.mode === 'SHARED' && (
            <PanelRow>
              <PanelLabel><FormattedMessage id="programAdmin.slides.shared" defaultMessage="Shared deck" /></PanelLabel>
              <div>
                {talk.sharedPublishedAt && (
                  <PersonMeta>
                    <FormattedMessage id="programAdmin.slides.publishedAt" defaultMessage="published {when}"
                      values={{ when: formatDateTime(intl, talk.sharedPublishedAt) }} />
                  </PersonMeta>
                )}
                <SpotActions>
                  {talk.sharedPublishedAt ? (
                    <MiniButton type="button" disabled={disabled}
                      onClick={() => setConfirming({
                        titleId: 'programAdmin.slides.unpublishSharedConfirmTitle', titleDef: 'Unpublish the shared deck?',
                        introId: 'programAdmin.slides.unpublishSharedConfirmIntro',
                        introDef: 'The shared deck stops being downloadable from the public program for every speaker covered by this entry. The file and its history stay.',
                        key: 'shared:unpublish',
                        task: () => unpublishSharedDeck(eventId, entryId),
                        result: {
                          successId: 'programAdmin.slides.sharedUnpublished', successDef: 'The shared deck was unpublished.',
                          failedId: 'programAdmin.slides.unpublishFailed', failedDef: 'Could not unpublish the deck.',
                          reload: true,
                        },
                      })}>
                      <FormattedMessage id="programAdmin.slides.unpublishShared" defaultMessage="Unpublish shared deck" />
                    </MiniButton>
                  ) : (
                    <MiniButton type="button" disabled={disabled || sharedBlockers.length > 0}
                      title={sharedBlockers.length > 0 ? sharedBlockers.join('\n') : undefined}
                      onClick={() => run('shared:publish',
                        () => publishSharedDeck(eventId, entryId),
                        {
                          successId: 'programAdmin.slides.sharedPublished', successDef: 'The shared deck was published.',
                          failedId: 'programAdmin.slides.publishFailed', failedDef: 'Could not publish the deck.',
                          reload: true,
                        })}>
                      <FormattedMessage id="programAdmin.slides.publishShared" defaultMessage="Publish shared deck" />
                    </MiniButton>
                  )}
                </SpotActions>
              </div>
            </PanelRow>
          )}

          {talk.titleSuggestion && (
            <PanelRow>
              <PanelLabel><FormattedMessage id="programAdmin.slides.suggestion" defaultMessage="Suggested title" /></PanelLabel>
              <div>
                <PersonName>{talk.titleSuggestion}</PersonName>
                <PersonMeta>
                  <FormattedMessage id="programAdmin.slides.suggestedBy" defaultMessage="Suggested by #{personId}{when}"
                    values={{
                      personId: talk.titleSuggestedByPersonId,
                      when: talk.titleSuggestedAt ? <> — <HistoryWhen>{formatDateTime(intl, talk.titleSuggestedAt)}</HistoryWhen></> : '',
                    }} />
                </PersonMeta>
                <SpotActions style={{ marginTop: '.35rem' }}>
                  {['EN', 'DE'].map((language) => (
                    <MiniButton key={language} type="button" disabled={disabled}
                      onClick={() => setConfirming({
                        titleId: 'programAdmin.slides.acceptConfirmTitle', titleDef: 'Replace the entry title?',
                        introId: 'programAdmin.slides.acceptConfirmIntro',
                        introDef: 'The entry’s {language} title is overwritten with what the speaker suggested, and the change appears in the program at once.',
                        introValues: { language },
                        key: `accept:${language}`,
                        task: () => acceptTitleSuggestion(eventId, entryId, language),
                        result: {
                          successId: 'programAdmin.slides.suggestionAccepted', successDef: 'The suggested title was taken.',
                          failedId: 'programAdmin.slides.suggestionAcceptFailed', failedDef: 'Could not take the suggested title.',
                          reload: true, changed: true,
                        },
                      })}>
                      <FormattedMessage id="programAdmin.slides.accept" defaultMessage="Accept {language}" values={{ language }} />
                    </MiniButton>
                  ))}
                  <MiniButton type="button" disabled={disabled}
                    onClick={() => setConfirming({
                      titleId: 'programAdmin.slides.dismissConfirmTitle', titleDef: 'Dismiss this suggestion?',
                      introId: 'programAdmin.slides.dismissConfirmIntro',
                      introDef: 'The suggested title is dropped; the entry keeps the organizer’s own title. The speaker is not told.',
                      key: 'dismiss',
                      task: () => dismissTitleSuggestion(eventId, entryId),
                      result: {
                        successId: 'programAdmin.slides.suggestionDismissed', successDef: 'The suggested title was dismissed.',
                        failedId: 'programAdmin.slides.suggestionDismissFailed', failedDef: 'Could not dismiss the suggested title.',
                        reload: true, changed: true,
                      },
                    })}>
                    <FormattedMessage id="programAdmin.slides.dismiss" defaultMessage="Dismiss" />
                  </MiniButton>
                </SpotActions>
              </div>
            </PanelRow>
          )}
        </>
      )}

      {actionError && !confirming && <PanelHint style={{ color: '#991b1b' }}>{actionError}</PanelHint>}
      {success && <PanelHint>{success}</PanelHint>}

      <ConfirmDialog
        open={!!confirming}
        tone="warning"
        title={confirming ? intl.formatMessage({ id: confirming.titleId, defaultMessage: confirming.titleDef }) : ''}
        intro={confirming ? intl.formatMessage({ id: confirming.introId, defaultMessage: confirming.introDef }, confirming.introValues) : ''}
        points={confirming?.points || []}
        confirmLabel={confirming ? intl.formatMessage({ id: confirming.confirmId || 'programAdmin.confirm.confirm', defaultMessage: confirming.confirmDef || 'Confirm' }) : ''}
        cancelLabel={intl.formatMessage({ id: 'programAdmin.confirm.cancel', defaultMessage: 'Cancel' })}
        error={actionError}
        busy={busy !== null}
        onConfirm={confirmed}
        onClose={() => { setConfirming(null); setActionError(null); }}
      />
    </PanelSection>
  );
};

export default SlidesPanel;
